const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface Feature {
  id: number;
  key: string;
  name: string;
  description?: string;
  category?: string;
  route_path?: string;
  menu_label?: string;
  menu_icon?: string;
  menu_order: number;
  is_menu_item: boolean;
  is_page: boolean;
  enabled: boolean;
  createdat: string;
  updatedat: string;
}

export interface MenuFeature {
  key: string;
  name: string;
  route_path: string;
  menu_label: string;
  menu_icon?: string;
  menu_order: number;
  category?: string;
  enabled: boolean;
}

export const FeatureService = {
  // Get all features (admin only)
  getAllFeatures: async (): Promise<Feature[]> => {
    try {
      const response = await fetch(`${API_URL}/features`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch all features:', error);
      throw error;
    }
  },

  // Get enabled features only
  getEnabledFeatures: async (): Promise<Feature[]> => {
    try {
      const response = await fetch(`${API_URL}/features/enabled`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch enabled features:', error);
      throw error;
    }
  },

  // Get menu features for sidebar generation
  getMenuFeatures: async (): Promise<MenuFeature[]> => {
    try {
      const response = await fetch(`${API_URL}/features/menu`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch menu features:', error);
      throw error;
    }
  },

  // Get features by category
  getFeaturesByCategory: async (category: string): Promise<Feature[]> => {
    try {
      const response = await fetch(`${API_URL}/features/category/${category}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error(`Failed to fetch features for category ${category}:`, error);
      throw error;
    }
  },

  // Get a specific feature by key
  getFeatureByKey: async (key: string): Promise<Feature | null> => {
    try {
      const response = await fetch(`${API_URL}/features/${key}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to fetch feature ${key}:`, error);
      throw error;
    }
  },

  // Update a feature (admin only)
  updateFeature: async (key: string, updates: Partial<Feature>): Promise<Feature> => {
    try {
      const response = await fetch(`${API_URL}/features/${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to update feature ${key}:`, error);
      throw error;
    }
  },

  // Toggle feature enabled/disabled (admin only)
  toggleFeature: async (key: string, enabled: boolean): Promise<Feature> => {
    try {
      const response = await fetch(`${API_URL}/features/${key}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify({ enabled })
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`Failed to toggle feature ${key}:`, error);
      throw error;
    }
  },

  // Create a new feature (admin only)
  createFeature: async (featureData: Omit<Feature, 'id' | 'createdat' | 'updatedat'>): Promise<Feature> => {
    try {
      const response = await fetch(`${API_URL}/features`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors',
        body: JSON.stringify(featureData)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create feature:', error);
      throw error;
    }
  },

  // Delete a feature (admin only)
  deleteFeature: async (key: string): Promise<void> => {
    try {
      const response = await fetch(`${API_URL}/features/${key}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error(`Failed to delete feature ${key}:`, error);
      throw error;
    }
  },

  // Get all categories
  getCategories: async (): Promise<string[]> => {
    try {
      const response = await fetch(`${API_URL}/features/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  }
};

export default FeatureService;